import { fullTaskRef } from './factory-changes';
import { findFencedBlock } from './factory-questions';

/**
 * T-27: tasks the coding agent creates because someone on the task asked it to. Its final message
 * carries one fenced block,
 *
 * ```factory-tasks
 * { "tasks": [{ "title": "Schedule agent updates", "description": "…", "column": "Backlog" }] }
 * ```
 *
 * When the run posts that message (the flow's task.comment step), the factory creates each task and
 * posts the message with the block replaced by the new tasks' refs (replaceFactoryTasksBlock).
 * Suggestions for people to decide on stay in factory-suggestions (T-14).
 *
 * SF-T-241: a task can be a sub-task: `parent` is an existing task's ref (in the run's project) or another
 * task's `id` in the same block, which is made first.
 */

export const FACTORY_TASKS_LANG = 'factory-tasks';

/** Most tasks one message makes, and characters per piece of text. */
export const FACTORY_TASKS_MAX = 5;
export const FACTORY_TASK_TITLE_MAX = 200;
export const FACTORY_TASK_DESCRIPTION_MAX = 8000;

/** The columns a created task may go in; Backlog unless the agent says Ready. */
export const FACTORY_TASK_COLUMNS = ['Backlog', 'Ready'] as const;
export type FactoryTaskColumn = (typeof FACTORY_TASK_COLUMNS)[number];

export interface FactoryTaskDraft {
  title: string;
  description: string;
  column: FactoryTaskColumn;
  /** Its name in the block, for another task's `parent`. */
  id?: string;
  /** "SF-T-240", or another task's id in the block. */
  parent?: string;
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

function column(v: unknown): FactoryTaskColumn {
  const name = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return FACTORY_TASK_COLUMNS.find((c) => c.toLowerCase() === name) ?? 'Backlog';
}

/** The block's JSON, checked and trimmed; null when it isn't usable. A bare list of tasks is accepted too. */
export function parseFactoryTasksJson(raw: string): FactoryTaskDraft[] | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = (Array.isArray(data) ? { tasks: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.tasks)) return null;
  const tasks: FactoryTaskDraft[] = [];
  const titles = new Set<string>();
  for (const item of obj.tasks) {
    if (tasks.length >= FACTORY_TASKS_MAX) break;
    const t = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    // Spaces collapsed the way the task write does (cleanTitle), so a retry finds the task by its title.
    const title = text(t.title, FACTORY_TASK_TITLE_MAX).replace(/\s+/g, ' ').trim();
    if (!title || titles.has(title.toLowerCase())) continue;
    titles.add(title.toLowerCase());
    const id = text(t.id, 60).replace(/\s+/g, ' ');
    const parent = fullTaskRef(t.parent) || text(t.parent, 60).replace(/\s+/g, ' ');
    tasks.push({
      title,
      description: text(t.description ?? t.detail, FACTORY_TASK_DESCRIPTION_MAX),
      column: column(t.column),
      ...(id ? { id } : {}),
      ...(parent ? { parent } : {}),
    });
  }
  return tasks.length ? tasks : null;
}

/** The first factory-tasks block in a message, with where it sits; null when there's none (or it's broken). */
export function findFactoryTasks(message: string): { tasks: FactoryTaskDraft[]; start: number; end: number } | null {
  const block = findFencedBlock(message, FACTORY_TASKS_LANG);
  const tasks = block ? parseFactoryTasksJson(block.body) : null;
  if (!block || !tasks) return null;
  return { tasks, start: block.start, end: block.end };
}

/**
 * The task in the same block a task's `parent` names by its id; undefined for a ref, no parent, or an id
 * no other task has, or more than one does (which it means can't be told).
 */
export function parentDraftIndex(drafts: readonly FactoryTaskDraft[], index: number): number | undefined {
  const parent = drafts[index]?.parent;
  if (!parent || fullTaskRef(parent)) return undefined;
  // Counted over the whole block, itself included: an id given twice anywhere can't say which it means.
  const matches = drafts.flatMap((d, i) => (d.id === parent ? [i] : []));
  return matches.length === 1 && matches[0] !== index ? matches[0] : undefined;
}

/** What creating the block's tasks came to: the ones made (or found from an earlier try), and the ones that failed. */
export interface FactoryTasksOutcome {
  /** `parent`: the ref of the task it's a sub-task of. */
  created: { ref: string; title: string; column: string; parent?: string }[];
  failed: { title: string; reason: string }[];
}

/** The marker at the end of a created task's description, which a repeat of the step looks for so it doesn't make the task twice. */
export function factoryTaskMarker(runNumber: number, fromRef: string, place?: { index: number; of: number }): string {
  // The place in the block makes each task's marker its own, so a retry finds it however it's been renamed.
  return `_Created by R-${runNumber} from ${fromRef}, when asked${place ? ` (${place.index}/${place.of})` : ''}._`;
}

/** The message with its factory-tasks block replaced by a short list of what was made. */
export function replaceFactoryTasksBlock(message: string, found: { start: number; end: number }, outcome: FactoryTasksOutcome): string {
  const body = message.replace(/\r\n?/g, '\n');
  const lines: string[] = [];
  if (outcome.created.length) {
    // T-27: they're drafts: nothing builds them until a person promotes them.
    lines.push(outcome.created.length === 1 ? '**New draft task** (promote it to have it built):' : '**New draft tasks** (promote them to have them built):');
    for (const t of outcome.created) lines.push(`- **${t.ref}** ${t.title} (${t.column}${t.parent ? `, a sub-task of ${t.parent}` : ''})`);
  }
  if (outcome.failed.length) {
    if (lines.length) lines.push('');
    lines.push(outcome.failed.length === 1 ? "**Couldn't create this task:**" : "**Couldn't create these tasks:**");
    for (const t of outcome.failed) lines.push(`- ${t.title}: ${t.reason}`);
  }
  return `${body.slice(0, found.start)}${lines.join('\n')}${body.slice(found.end)}`.trim();
}
